import { FC, useState } from 'react'
import { inflect } from 'inflection'

import {
  Text,
  GreyGrey90,
  InputSelectControl,
  NumberFieldInput,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import { SendReminder } from '../..'

type SendReminderSelectorProps = {
  value: SendReminder | undefined
  onChange: (value: SendReminder | undefined) => void
}

const TEST_ID = 'automations.editor.configForm.reminder.sendReminder'

const CUSTOM_OPTION_VALUE = 'CUSTOM'

const REMINDER_DAY_PRESETS = [1, 7, 14, 21, 28] as const

const DEFAULT_CUSTOM_DAYS = 7

// Reminders are anchored to an invoice's due date, so a duration beyond a year
// is never meaningful — cap custom input at 365 days.
const MAX_CUSTOM_DURATION_DAYS = 365

// Reminders chase invoices that have already gone overdue, so an unset
// reminder defaults its direction to "after due date".
const DEFAULT_DIRECTION: SendReminder['type'] = 'AFTER_DUE_DATE'

const formatDaysLabel = (days: number) => `${days} ${inflect('day', days)}`

const DURATION_OPTIONS = [
  ...REMINDER_DAY_PRESETS.map(days => ({
    value: String(days),
    label: formatDaysLabel(days)
  })),
  { value: CUSTOM_OPTION_VALUE, label: 'Custom' }
]

const DIRECTION_OPTIONS = [
  { value: 'BEFORE_DUE_DATE', label: 'before due date' },
  { value: 'AFTER_DUE_DATE', label: 'after due date' }
] as const satisfies ReadonlyArray<{
  value: SendReminder['type']
  label: string
}>

const DIRECTION_SELECT_OPTIONS = DIRECTION_OPTIONS.map(({ value, label }) => ({
  value,
  label
}))

const isPreset = (days: number | undefined): boolean =>
  REMINDER_DAY_PRESETS.includes(days as (typeof REMINDER_DAY_PRESETS)[number])

export const SendReminderSelector: FC<SendReminderSelectorProps> = ({
  value,
  onChange
}) => {
  const [direction, setDirection] = useState<SendReminder['type']>(
    value?.type ?? DEFAULT_DIRECTION
  )
  const [days, setDays] = useState<number | undefined>(value?.duration.days)
  // Custom mode reveals a free number input beside the dropdown. A value whose
  // day count isn't one of the presets is inherently custom.
  const [isCustom, setIsCustom] = useState(
    Boolean(value) && !isPreset(value?.duration.days)
  )

  // The committed reminder mirrors the draft direction + days. An empty or zero
  // day count unsets it rather than committing an invalid 0-day duration.
  const commit = (nextDays: number | undefined, nextDirection = direction) => {
    setDays(nextDays)
    setDirection(nextDirection)
    onChange(
      nextDays
        ? { type: nextDirection, duration: { days: nextDays } }
        : undefined
    )
  }

  const handleDurationSelect = (selected: string) => {
    if (selected === CUSTOM_OPTION_VALUE) {
      setIsCustom(true)
      commit(days ?? DEFAULT_CUSTOM_DAYS)
      return
    }

    setIsCustom(false)
    commit(Number(selected))
  }

  const handleDaysChange = (raw: string) => {
    commit(
      raw
        ? Math.min(Math.max(parseInt(raw), 0), MAX_CUSTOM_DURATION_DAYS)
        : undefined
    )
  }

  // The preset dropdown stays visible in custom mode (showing "Custom") so the
  // chevron remains the way back to a preset; the days input sits beside it.
  const presetValue = isPreset(days) ? String(days) : ''
  const durationSelectValue = isCustom ? CUSTOM_OPTION_VALUE : presetValue

  return (
    <Flex
      data-testid={`${TEST_ID}.control`}
      gap="8px"
      alignItems="center"
      {...Primary13Regular}
      color={GreyGrey90}
    >
      <Box width="120px">
        <InputSelectControl
          data-testid={TEST_ID}
          options={DURATION_OPTIONS}
          value={durationSelectValue}
          placeholder="Select"
          onChange={handleDurationSelect}
          filterable={false}
        />
      </Box>

      {isCustom && (
        <Flex gap="8px" alignItems="center">
          <NumberFieldInput
            autoFocus
            preventScrollOnAutoFocus
            data-testid={`${TEST_ID}.days`}
            height="32px"
            width="56px"
            min={1}
            max={MAX_CUSTOM_DURATION_DAYS}
            value={days ?? ''}
            onChange={e => handleDaysChange(e.target.value)}
          />
          <Text {...Primary13Regular} color={GreyGrey90}>
            days
          </Text>
        </Flex>
      )}

      <Box width="150px">
        <InputSelectControl
          data-testid={`${TEST_ID}.direction`}
          options={DIRECTION_SELECT_OPTIONS}
          value={direction}
          onChange={selected => commit(days, selected as SendReminder['type'])}
          filterable={false}
        />
      </Box>
    </Flex>
  )
}
